import React, { useEffect, useRef } from "react";
import "../../styles/Home.css";

/* Tom's compact visual How It Works (HowitWorks.html, 19/07/2026): each step
   shows a mini mock of the product instead of a paragraph, sized to stay
   glanceable on phones. Scroll-reveal is progressive enhancement only — the
   page is prerendered by react-snap, so everything is visible by default and
   the hidden state is only armed once JS mounts and adds .hiw-js (same
   pattern as LearnMore). */
const HowItWorks = () => {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof window === "undefined") return undefined;

    const isPrerendering =
      typeof navigator !== "undefined" &&
      navigator.userAgent.includes("ReactSnap");
    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (
      isPrerendering ||
      prefersReducedMotion ||
      typeof IntersectionObserver === "undefined"
    ) {
      return undefined;
    }

    root.classList.add("hiw-js");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("hiw-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    root.querySelectorAll(".hiw-reveal").forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      root.classList.remove("hiw-js");
    };
  }, []);

  return (
    <div className="section how-it-works" ref={rootRef}>
      <div className="feature-panel bg-white border border-[#E8E8EC] rounded-[22px] p-4 md:p-10 lg:p-12">
        {/* Section header */}
        <div className="hiw-reveal text-center max-w-2xl mx-auto mb-5 md:mb-12">
          <h2 className="feature-title text-2xl md:text-5xl font-extrabold text-gray-900 mb-2 md:mb-4 tracking-tight leading-tight">
            How it works
          </h2>
          <p className="feature-copy text-sm md:text-xl text-gray-600 font-medium leading-relaxed">
            Describe the work, explore an indicative estimate, then find
            tradespeople to discuss a written quote.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-10">
          {/* Step 1 — describe it in the chat */}
          <div className="hiw-reveal flex flex-col items-center text-center min-w-0">
            <div className="hiw-num-muted w-6 h-6 md:w-8 md:h-8 bg-gray-100 rounded-full flex items-center justify-center text-xs md:text-sm font-bold text-gray-400 mb-1 md:mb-2">
              1
            </div>
            <h3 className="feature-title text-[17px] leading-tight md:text-2xl font-extrabold text-gray-900 tracking-tight mb-2 md:mb-4">
              Describe your project
            </h3>
            <div
              className="w-full h-24 md:h-32 bg-[#f8fafc] rounded-2xl border border-gray-100 flex flex-col justify-between p-2 md:p-3 shadow-inner"
              aria-hidden="true"
            >
              <div className="flex flex-col gap-1 md:gap-1.5 mt-0.5">
                <div className="bg-[#FFBD59] text-gray-900 text-[9px] md:text-[10px] font-bold py-1 px-2 md:py-1.5 md:px-3 rounded-xl rounded-tr-none self-end shadow-sm">
                  I need a new patio
                </div>
                <div className="bg-white border border-gray-200 text-gray-600 text-[9px] md:text-[10px] font-bold py-1 px-2 md:py-1.5 md:px-3 rounded-xl rounded-tl-none self-start shadow-sm">
                  How big is the area?
                </div>
              </div>
              {/* Mini input field */}
              <div className="w-full bg-white border border-gray-200 rounded-full h-6 md:h-7 flex items-center px-2 shadow-sm mt-1 md:mt-2">
                <span className="text-[9px] md:text-[10px] text-gray-700 font-medium ml-1">
                  35 sq metres
                </span>
                <div className="w-0.5 h-2.5 md:h-3 bg-gray-400 animate-pulse ml-0.5 rounded-full"></div>
                <div className="ml-auto w-3.5 h-3.5 md:w-4 md:h-4 bg-[#FFBD59] rounded-full flex items-center justify-center shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2.5"
                    stroke="currentColor"
                    className="w-2 h-2 md:w-2.5 md:h-2.5 text-gray-900"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 — instant price */}
          <div className="hiw-reveal hiw-delay-1 flex flex-col items-center text-center min-w-0">
            <div className="w-6 h-6 md:w-8 md:h-8 bg-[#FFBD59] rounded-full flex items-center justify-center text-xs md:text-sm font-bold text-gray-900 mb-1 md:mb-2 shadow-sm">
              2
            </div>
            <h3 className="feature-title text-[17px] leading-tight md:text-2xl font-extrabold text-gray-900 tracking-tight mb-2 md:mb-4">
              Explore your estimate
            </h3>
            <div
              className="w-full h-24 md:h-32 bg-[#f8fafc] rounded-2xl border border-gray-100 flex flex-col items-center justify-center px-2 md:px-3 shadow-inner"
              aria-hidden="true"
            >
              <div className="bg-white border-2 border-[#FFBD59] rounded-xl px-3 py-2 md:px-4 md:py-3 text-center shadow-md w-full max-w-[160px] md:max-w-[180px]">
                <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-0.5 md:mb-1">
                  Example estimate
                </div>
                <div className="text-lg md:text-xl font-black text-gray-900 leading-tight">
                  £4.2k - £5.5k
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 — local trades */}
          <div className="hiw-reveal hiw-delay-2 flex flex-col items-center text-center min-w-0">
            <div className="hiw-num-dark w-6 h-6 md:w-8 md:h-8 bg-gray-900 rounded-full flex items-center justify-center text-xs md:text-sm font-bold text-white mb-1 md:mb-2 shadow-sm">
              3
            </div>
            <h3 className="feature-title text-[17px] leading-tight md:text-2xl font-extrabold text-gray-900 tracking-tight mb-2 md:mb-4">
              Find local trades
            </h3>
            <div
              className="w-full h-24 md:h-32 bg-[#f8fafc] rounded-2xl border border-gray-100 flex flex-col justify-center px-3 md:px-4 gap-1.5 md:gap-2.5 shadow-inner"
              aria-hidden="true"
            >
              <div className="bg-white p-1.5 md:p-2 rounded-xl border border-gray-200 shadow-sm flex items-center gap-2.5 md:gap-3 w-full">
                <div className="w-6 h-6 md:w-7 md:h-7 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center text-[9px] md:text-[10px] font-bold shrink-0">
                  EP
                </div>
                <div className="flex flex-col items-start leading-tight">
                  <span className="text-[11px] md:text-xs font-bold text-gray-900">
                    Example paving profile
                  </span>
                  <span className="text-[9px] md:text-[10px] text-[#d97706] font-bold mt-0.5">
                    Illustrative profile
                  </span>
                </div>
              </div>
              <div className="bg-white p-1.5 md:p-2 rounded-xl border border-gray-200 shadow-sm flex items-center gap-2.5 md:gap-3 w-full">
                <div className="w-6 h-6 md:w-7 md:h-7 rounded-lg bg-green-100 text-green-600 flex items-center justify-center text-[9px] md:text-[10px] font-bold shrink-0">
                  SL
                </div>
                <div className="flex flex-col items-start leading-tight">
                  <span className="text-[11px] md:text-xs font-bold text-gray-900">
                    Example landscaping profile
                  </span>
                  <span className="text-[9px] md:text-[10px] text-[#d97706] font-bold mt-0.5">
                    Illustrative profile
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
